import * as React from 'react';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { Column, DataTableView, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { FormatService } from 'src/core/services/format.service';
import { ObjectUtils } from 'src/core/utils/object';
import { TextView } from 'src/core/ui/text.view';
import { InvoiceEntryList } from 'src/app/accounting/shared/invoices/list/parts/invoice-entry-list';
import { Button } from 'antd';
import { SummaryTableHeader } from 'src/app/accounting/shared/summary-table-header';

interface Props extends ComponentViewProps<DataSet<InvoiceSummary>> {
	onDeleteInvoice?: (invoice: InvoiceSummary) => void;
	isManagementMode?: boolean;
	invoicePayments?: { [invoiceId: number]: SelectableInvoicePaymentPayload };
	onChangeSelection?: (selectedIds: number[]) => void;
	onEditPayment?: (invoiceId: number, field: keyof InvoicePaymentPayload, value: any) => void;
}

@observer
export class InvoiceList extends React.Component<Props, {}> {
	@resolve
	private formatService: FormatService;

	private rowKey = (record: InvoiceSummary): string => record.id.toString();

	private getCheckedItemsSum = (selector: (value: InvoiceSummary) => number): string => {
		const { model, invoicePayments } = this.props;
		const items = model.items.slice();

		const supplierCurrencyFormat = items.length > 0 ? items[0].currencyFormat : '${0}';

		const total = items
			.filter(m => invoicePayments == null || (invoicePayments[m.id] != null && invoicePayments[m.id].checked))
			.reduce((prev, curr) => prev + selector(curr), 0);

		return this.formatService.toString('currency', ObjectUtils.round(total, 2), supplierCurrencyFormat);
	};

	private getTotalNetSum = (): string => this.getCheckedItemsSum((entry) => entry.totalNet);
	private getPaidSum = (): string => this.getCheckedItemsSum((entry) => entry.paid);

	private renderItemsPerLine(items: any[]): React.ReactNode {
		return items.map((m, index) => (
			<React.Fragment key={index}>
				{index > 0 && <br />}
				{m}
			</React.Fragment>
		));
	}

	@autobind
	private getCheckboxProps(item: InvoiceSummary): Object {
		return {
			disabled: item.status > 1
		};
	}

	@autobind
	private handleChangeSelection(selectedIds: string[]): void {
		const selectedInvoicesIds = selectedIds.map(m => parseInt(m));
		this.props.onChangeSelection(selectedInvoicesIds);
	}

	@autobind
	private handleExpandedRowRender(entry: InvoiceSummary): React.ReactNode {
		return (
			<InvoiceEntryList
				model={entry}
				setBusy={this.props.setBusy}
			/>
		);
	}

	render(): React.ReactNode {
		const { model, onDeleteInvoice, isManagementMode, invoicePayments, onChangeSelection, onEditPayment } = this.props;
		if (model == null) {
			return null;
		}

		const items = model.items.slice();
		const selectedRowKeys = items
			.filter(m => invoicePayments == null || (invoicePayments[m.id] != null && invoicePayments[m.id].checked))
			.map(m => m.id.toString());

		let columns: Column[] = [];
		columns.push(
			{
				title: 'Number',
				key: 'invoiceNumber',
				dataIndex: 'invoiceNumber'
			},
			{
				title: 'Invoice Reference Number',
				key: 'invoiceReferenceNumber',
				dataIndex: 'invoiceReferenceNumber'
			},
			{
				title: 'Supplier',
				key: 'supplierTitle',
				dataIndex: 'supplierTitle'
			},
			{
				title: 'Invoice Date',
				key: 'billingDate',
				dataIndex: 'billingDate',
				render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
			},
			{
				title: 'Deadline',
				key: 'dueDate',
				dataIndex: 'dueDate',
				render: (field: Date) => this.renderItemsPerLine([this.formatService.toString('date', field, 'utc'), this.formatService.toString('time', field, 'utc')])
			},
			{
				title: 'Currency',
				key: 'currencyIsoCode',
				dataIndex: 'currencyIsoCode'
			},
			{
				title: 'Total Net',
				key: 'totalNet',
				dataIndex: 'totalNet',
				render: (field: number, record: InvoiceSummary) => this.formatService.toString('currency', field, record.currencyFormat)
			},
			{
				title: 'Total Gross',
				key: 'totalGross',
				dataIndex: 'totalGross',
				render: (field: number, record: InvoiceSummary) => this.formatService.toString('currency', field, record.currencyFormat)
			},
			{
				title: 'Paid',
				key: 'paid',
				dataIndex: 'paid',
				render: (field: any, record: InvoiceSummary) => this.formatService.toString('currency', field, record.currencyFormat),
			}
		);

		if (onEditPayment != null) {
			columns.push({
				title: 'Description',
				key: 'description',
				editable: (field: string, record: InvoiceSummary) => {
					if (invoicePayments[record.id] == null) {
						return null;
					}

					const isSelected = (record.totalNet - record.paid) === invoicePayments[record.id].amount;
					const { description } = invoicePayments[record.id].otherProps;

					return (
						<TextView
							value={description}
							onChanged={(value) => {
								onEditPayment(record.id, 'otherProps', { ...invoicePayments[record.id].otherProps, description: value });
							}}
							rows={2}
							mode={isSelected ? 'edit' : 'display'}
						/>
					);
				}
			});
		}

		if (onDeleteInvoice != null) {
			columns.push({
				title: 'Options',
				key: 'option',
				render: (_, record: InvoiceSummary) => (
					<Button
						icon="rest"
						onClick={() => onDeleteInvoice(record)}>
						Delete
					</Button>
				)
			});
		}

		return (
			<React.Fragment>
				{
					onChangeSelection &&
					<SummaryTableHeader
						fields={[
							{ label: 'TotalNet', value: this.getTotalNetSum() },
							{ label: 'Paid', value: this.getPaidSum() }
						]}
					/>
				}
				<DataTableView
					mode={isManagementMode ? 'edit' : 'display'}
					rowKey={this.rowKey}
					model={model}
					expandedRowRender={this.handleExpandedRowRender}
					columns={columns}
					setBusy={this.props.setBusy}
					hideToolbar={isManagementMode ? undefined : (ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination)}
					selection={isManagementMode
						? undefined
						: onChangeSelection != null
							? {
								selectedRowKeys: selectedRowKeys,
								getCheckboxProps: this.getCheckboxProps,
								onChange: this.handleChangeSelection
							}
							: false
					}
					otherProps={{
						scroll: { x: 1000 }
					}}
				/>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/shared/invoices/list/invoice-list.tsx